import React, { useState } from "react";
import ComponentCustomModal from "../../../components/common/customModal/ComponentCustomModal";
import { Field, FieldArray, Form, Formik } from "formik";
import { FtextField } from "../../../components/common/FtextField";
import Loader from "../../../components/common/Loader";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Tab, Tabs } from "react-bootstrap";
import useDataInputOption from "../../../hooks/useDataInputOption";
import { useDispatch, useSelector } from "react-redux";
import { DashCircle, PlusCircle } from "react-bootstrap-icons";
import {
  bandwidthResellerSubmit,
  updateBandwidthResellerSubmit,
} from "../../../features/apiCallReseller";
import useISPowner from "../../../hooks/useISPOwner";

const ResellerDialog = ({ show, setShow, isUpdate, reseller }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //validator
  const validationSchema = Yup.object({
    name: Yup.string().required(t("enterName")),
    mobile: Yup.string()
      .min(11, t("write11DigitMobileNumber"))
      .max(11, t("over11DigitMobileNumber"))
      .required(t("writeMobileNumber")),
    email: Yup.string().email(t("incorrectEmail")),
    nid: Yup.string(),
    address: Yup.string(),
    status: Yup.string().required(t("selectStatus")),
  });

  // call the data input option function
  const inputPermission = {
    name: true,
    mobile: true,
    address: true,
    email: true,
    nid: true,
    status: true,
  };

  // get data input option from useDataInputOption hook
  const dataInputOption = useDataInputOption(
    inputPermission,
    null,
    null,
    isUpdate ? reseller : null
  );

  //---> Get user & current user data form useISPOwner hooks
  const { ispOwnerId } = useISPowner();

  //---> Local State
  const [isLoading, setIsLoading] = useState(false);

  const handleBandwidthResellerSubmit = (formValues) => {
    const sendData = {
      ...formValues,
      ispOwner: ispOwnerId,
    };

    if (isUpdate) {
      updateBandwidthResellerSubmit(
        dispatch,
        reseller?._id,
        sendData,
        setIsLoading,
        setShow
      );
    } else {
      bandwidthResellerSubmit(dispatch, sendData, setIsLoading, setShow);
    }
  };

  return (
    <>
      <ComponentCustomModal
        show={show}
        setShow={setShow}
        centered={false}
        size={"lg"}
        header={t("addNewBandwidth")}
      >
        <Formik
          initialValues={{
            ...dataInputOption?.inputInitialValues,
            nttn: isUpdate ? reseller.nttn : "",
            linkId: isUpdate ? reseller.linkId : "",
            activationDate: isUpdate
              ? new Date(reseller.activationDate)
              : new Date(),
            vlan: isUpdate
              ? reseller.vlan
              : [
                  {
                    name: "",
                    id: "",
                  },
                ],
            ipAddress: isUpdate ? reseller.ipAddress : [""],
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleBandwidthResellerSubmit(values);
          }}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="displayGrid">
                <Tabs
                  defaultActiveKey={"basic"}
                  id="uncontrolled-tab-example"
                  className="mb-3"
                >
                  <Tab eventKey="basic" title={t("profile")}>
                    <div className="d-flex justify-content-center">
                      <div className="displayGrid col-6">
                        {dataInputOption?.inputOption.map(
                          (item) => item?.isVisible && <FtextField {...item} />
                        )}
                      </div>
                    </div>
                  </Tab>

                  <Tab eventKey="bandwidth" title={t("linkInformation")}>
                    <div className="d-flex justify-content-center">
                      <div className="displayGrid col-8">
                        <FtextField
                          label={t("nttnInfo")}
                          type="text"
                          name="nttn"
                        />

                        <div className="displayGrid1">
                          <FtextField
                            label={t("scrLinkId")}
                            type="text"
                            name="linkId"
                          />

                          <FtextField
                            {...{
                              name: "activationDate",
                              type: "date",
                              id: "activationDate",
                              label: t("activationDate"),
                              placeholderText: "YYYY MM DD HH:mm A",
                              component: "DatePicker",
                              dateFormat: "yyyy MM dd hh:mm a",
                              showTimeSelect: "showTimeSelect",
                            }}
                          />
                        </div>

                        <FieldArray name="vlan">
                          {({ push, remove }) => (
                            <div>
                              <label className="changeLabelFontColor">
                                {t("vlanInfo")}
                              </label>

                              {values.vlan.map((_, index) => (
                                <div
                                  key={index}
                                  className="d-flex align-items-center gap-2 w-100"
                                >
                                  <div className="flex-fill">
                                    <FtextField
                                      type="text"
                                      name={`vlan.${index}.name`}
                                      placeholder={t("vlanName")}
                                      className="flex-fill form-control"
                                    />
                                  </div>

                                  <div className="flex-fill">
                                    <FtextField
                                      type="text"
                                      name={`vlan.${index}.id`}
                                      placeholder={t("vlanId")}
                                      className="flex-fill form-control"
                                    />
                                  </div>

                                  <div>
                                    {index + 1 === values.vlan.length ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() =>
                                          push({
                                            name: "",
                                            id: "",
                                          })
                                        }
                                      >
                                        <PlusCircle size={20} />
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => remove(index)}
                                      >
                                        <DashCircle size={20} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </FieldArray>

                        <FieldArray name="ipAddress">
                          {({ push, remove }) => (
                            <div>
                              <label className="changeLabelFontColor">
                                {t("ipAddress")}
                              </label>

                              {values.ipAddress.map((_, index) => (
                                <div
                                  key={index}
                                  className="d-flex align-items-center gap-2 w-100"
                                >
                                  <div className="flex-fill">
                                    <FtextField
                                      type="text"
                                      name={`ipAddress.${index}`}
                                      className="flex-fill form-control"
                                    />
                                  </div>

                                  <div className="">
                                    {index + 1 === values.ipAddress.length ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() => push("")}
                                      >
                                        <PlusCircle size={20} />
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => remove(index)}
                                      >
                                        <DashCircle size={20} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    </div>
                  </Tab>
                </Tabs>
              </div>

              <div className="displayGrid1 float-end mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={isLoading}
                  onClick={() => setShow(false)}
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  className="btn btn-success customBtn"
                  disabled={isLoading}
                >
                  {isLoading ? <Loader /> : t("save")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </ComponentCustomModal>
    </>
  );
};

export default ResellerDialog;
